import { AfterContentChecked, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { MatDialogRef } from '@angular/material/dialog';

import { Store } from '@ngrx/store';

import { ToastrService } from 'ngx-toastr';

import { Observable, Subscription } from 'rxjs';

import { v4 as uuidv4 } from 'uuid';

import { AppState } from 'src/app/state';
import { Template } from 'src/app/models/template';
import { Section, SectionType } from 'src/app/models/section';
import { Subsection } from 'src/app/models/subsection';
import { isThereEmptyTranslation } from 'src/app/dashboard/studies/helpers/translation-helper';
import { selectSelectedTemplate } from 'src/app/dashboard/state/templates/templates.selectors';
import {
  selectIsLoadingSection,
  selectSectionLoaded,
  selectSections,
  selectSelectedSection,
} from 'src/app/dashboard/state/sections/sections.selectors';
import { selectSelectedSubsection } from 'src/app/dashboard/state/subsections/subsections.selectors';
import { createSection, updateSection } from 'src/app/dashboard/state/sections/sections.actions';
import { UploadIds, UploadKeys } from 'src/app/models/upload';
import { IMAGE_SIZES } from 'src/app/core/dashboard-constants';
import { getCalculatedVariablesForStudy } from 'src/app/dashboard/state/calculated-variable/calculated-variable.actions';
import { getTemplateConfiguration } from 'src/app/dashboard/state/template-config/template-config.actions';
import {
  CONTENT_TEXT_IDENTIFIER,
  SECTION_BUTTON_OPTIONS,
  SECTION_BUTTON_TEXT_IDENTIFIER,
} from './description-section/description-section.component';

@Component({
  selector: 'dashboard-section-dialog',
  templateUrl: './section-dialog.component.html',
  styleUrls: ['./section-dialog.component.scss'],
})
export class SectionDialogComponent implements OnInit, OnDestroy, AfterContentChecked {
  sectionForm: FormGroup;

  isLoadingSection$: Observable<boolean>;
  sectionLoaded$: Observable<boolean>;

  selectedTemplate: Template;
  sections: Section[];
  selectedSection: Section;
  selectedSubsection: Subsection;

  isEdit = false;
  isEditingItem: boolean;
  sectionCoreFormSubmitted = false;
  isEditingConfiguration = false;

  generalTabActive = true;

  imgPath: string;
  isImageToEdit: boolean;
  idsForUpload: UploadIds;
  keyForUpload = UploadKeys.UPLOAD_FROM_TEMPLATE;
  IMAGE_SIZES = IMAGE_SIZES;

  subsectionIconUuid: string;
  isIconToEdit: boolean;

  SectionType = SectionType;

  private subscriptions = new Subscription();

  constructor(
    private formBuilder: FormBuilder,
    private store: Store<AppState>,
    private toastrService: ToastrService,
    private changeDetectorRef: ChangeDetectorRef,
    private sectionDialog: MatDialogRef<SectionDialogComponent>
  ) {}

  ngOnInit(): void {
    this.sectionForm = this.formBuilder.group({
      sectionDialogCore: [],
      imageUrl: [null],
      descriptionSection: [],
    });

    this.subsectionIconUuid = uuidv4();

    this.isLoadingSection$ = this.store.select(selectIsLoadingSection);
    this.sectionLoaded$ = this.store.select(selectSectionLoaded);

    this.subscriptions.add(
      this.store.select(selectSelectedTemplate).subscribe((template) => {
        this.selectedTemplate = template;
        this.idsForUpload = {
          studyId: this.selectedTemplate?.studyId,
        };
      })
    );

    this.subscriptions.add(this.store.select(selectSections).subscribe((sections) => (this.sections = sections)));

    this.subscriptions.add(
      this.store.select(selectSelectedSection).subscribe((section) => {
        this.selectedSection = section;
        this.isEdit = !!section;
        if (this.isEdit) {
          this.sectionDialogCore.disable();
          if (this.selectedSection?.imageUrl) {
            this.isImageToEdit = true;
            this.imgPath = this.selectedSection?.imageUrl;
            this.imageUrl.patchValue(this.selectedSection?.imageUrl);
          }
        }
      })
    );
    this.subscriptions.add(
      this.store.select(selectSelectedSubsection).subscribe((subsection) => {
        this.selectedSubsection = subsection;
        this.isEditingItem = !!this.selectedSubsection;
      })
    );
  }

  get sectionDialogCore(): AbstractControl {
    return this.sectionForm.get('sectionDialogCore') as AbstractControl;
  }

  get imageUrl(): AbstractControl {
    return this.sectionForm.get('imageUrl') as AbstractControl;
  }

  get descriptionSection(): AbstractControl {
    return this.sectionForm.get('descriptionSection') as AbstractControl;
  }

  ngAfterContentChecked() {
    this.changeDetectorRef.detectChanges();
  }

  saveSection() {
    this.sectionForm.markAllAsTouched();
    this.sectionDialogCore.markAllAsTouched();

    if (!this.sectionDialogCore.value.sectionName) {
      this.toastrService.error('Section Name is required');
      return;
    }

    if (this.sectionForm.invalid) {
      return;
    }

    this.sectionCoreFormSubmitted = true;

    let sectionDetails = {
      ...this.sectionForm.value.sectionDialogCore,
      imageUrl: this.imageUrl.value,
      feedbackTemplateId: this.selectedTemplate.id,
      type:
        this.sectionDialogCore.value.type[0].toLowerCase() +
        this.sectionDialogCore.value.type.slice(1).replace(' ', '_'),
      translations: this.sectionDialogCore.value.translations,
    };

    if (this.isEdit) {
      sectionDetails = {
        ...sectionDetails,
        id: this.selectedSection.id,
        sortOrder: this.selectedSection.sortOrder,
      };
      this.store.dispatch(updateSection({ section: sectionDetails }));
      this.sectionDialog.close();
    } else {
      sectionDetails = {
        ...sectionDetails,
        created: true,
        sortOrder: this.sections?.length,
      };
      this.store.dispatch(createSection({ section: sectionDetails }));
    }
  }

  saveDescriptionSection() {
    this.sectionForm.markAllAsTouched();

    if (this.sectionForm.invalid) {
      return;
    }

    if (!this.sectionDialogCore.value.sectionName) {
      this.toastrService.error('Section Name is required');
      return;
    }

    const allTranslations = [
      ...this.sectionDialogCore.value.translations,
      ...this.descriptionSection.value.translations,
    ];

    if (
      isThereEmptyTranslation(allTranslations, CONTENT_TEXT_IDENTIFIER) ||
      isThereEmptyTranslation(allTranslations, SECTION_BUTTON_TEXT_IDENTIFIER)
    ) {
      this.toastrService.info(
        'Please come back later to populate Section Title, Content Text and Button Text for all supported languages, in order to have a proper feedback'
      );
    }

    if (!this.descriptionSection.value.chosenOption) {
      this.toastrService.info('Please come back later to choose one of the options - Hyperlink or Section button');
    }

    if (
      this.descriptionSection.value.chosenOption === SECTION_BUTTON_OPTIONS.HYPERLINK &&
      !this.descriptionSection.value.hyperlink
    ) {
      this.toastrService.info('Please come back later to add hyperlink');
    }

    if (
      this.descriptionSection.value.chosenOption === SECTION_BUTTON_OPTIONS.LINK_TO_SECTION &&
      !this.descriptionSection.value.linkToSection
    ) {
      this.toastrService.info('Please come back later to add link to section');
    }

    let sectionDetails = {
      ...this.sectionForm.value.sectionDialogCore,
      ...this.sectionForm.value.descriptionSection,
      feedbackTemplateId: this.selectedTemplate.id,
      type:
        this.sectionDialogCore.value.type[0].toLowerCase() +
        this.sectionDialogCore.value.type.slice(1).replace(' ', '_'),
      translations: allTranslations,
    };

    this.descriptionSection.value.chosenOption === SECTION_BUTTON_OPTIONS.HYPERLINK
      ? (sectionDetails = {
          ...sectionDetails,
          linkToSection: null,
        })
      : (sectionDetails = {
          ...sectionDetails,
          hyperlink: null,
        });

    if (this.isEdit) {
      sectionDetails = {
        ...sectionDetails,
        id: this.selectedSection.id,
        sortOrder: this.selectedSection.sortOrder,
      };
      this.store.dispatch(updateSection({ section: sectionDetails }));
    } else {
      sectionDetails = {
        ...sectionDetails,
        created: true,
        sortOrder: this.sections?.length,
      };
      this.store.dispatch(createSection({ section: sectionDetails }));
    }
    this.sectionDialog.close();
  }

  prepareConfiguration(subsectionChanged: boolean) {
    if (subsectionChanged) {
      this.store.dispatch(getCalculatedVariablesForStudy({ studyId: this.selectedTemplate.studyId }));
      this.store.dispatch(
        getTemplateConfiguration({
          studyId: this.selectedTemplate.studyId,
          templateId: this.selectedTemplate.id,
          profileId: this.selectedSubsection.id,
        })
      );
    }
  }

  setIsConfigurationSaved(isConfigurationSaved: boolean) {
    this.isEditingConfiguration = !isConfigurationSaved;
  }

  resetSubsection(isReset: boolean) {
    this.isIconToEdit = !isReset;
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  tabChanged(tabChangeEvent: MatTabChangeEvent): void {
    this.generalTabActive = tabChangeEvent.index !== 1;

    if (tabChangeEvent.index === 1) {
      this.isEditingItem = false;
      this.isEditingConfiguration = true;
    }
  }
}
